import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  SmartToy,
  Save,
  Cancel
} from '@mui/icons-material'
import axios from 'axios'

const ContextosIA = () => {
  const [contextos, setContextos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [editingContexto, setEditingContexto] = useState(null)
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    contexto_institucional: '',
    organismos_rectores: '',
    normatividad_clave: '',
    marco_educativo: '',
    planeacion_nacional: '',
    marco_administrativo: '',
    activo: true
  })

  useEffect(() => {
    loadContextos()
  }, [])

  const loadContextos = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/contextos-ia', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setContextos(response.data || [])
    } catch (error) {
      console.error('Error al cargar contextos de IA:', error)
      setError('Error al cargar contextos: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingContexto(null)
    setFormData({
      nombre: '',
      descripcion: '',
      contexto_institucional: '',
      organismos_rectores: '',
      normatividad_clave: '',
      marco_educativo: '',
      planeacion_nacional: '',
      marco_administrativo: '',
      activo: true
    })
    setOpenDialog(true)
  }

  const handleEdit = (contexto) => {
    setEditingContexto(contexto)
    setFormData({
      nombre: contexto.nombre || '',
      descripcion: contexto.descripcion || '',
      contexto_institucional: contexto.contexto_institucional || '',
      organismos_rectores: contexto.organismos_rectores || '',
      normatividad_clave: contexto.normatividad_clave || '',
      marco_educativo: contexto.marco_educativo || '',
      planeacion_nacional: contexto.planeacion_nacional || '',
      marco_administrativo: contexto.marco_administrativo || '',
      activo: contexto.activo !== false
    })
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingContexto(null)
    setError(null)
  }

  const handleSubmit = async () => {
    if (!formData.nombre.trim()) {
      setError('El nombre del contexto es requerido')
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')

      if (editingContexto) {
        await axios.put(`/api/contextos-ia/${editingContexto.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Contexto actualizado exitosamente')
      } else {
        await axios.post('/api/contextos-ia', formData, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setSuccess('Contexto creado exitosamente')
      }

      handleCloseDialog()
      loadContextos()
    } catch (error) {
      setError('Error al guardar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleDelete = async (contexto) => {
    if (!window.confirm(`¿Está seguro de eliminar el contexto "${contexto.nombre}"?`)) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/contextos-ia/${contexto.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('Contexto eliminado exitosamente')
      loadContextos()
    } catch (error) {
      setError('Error al eliminar: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleToggleActive = async (contexto) => {
    try {
      const token = localStorage.getItem('sgc_token')
      await axios.put(`/api/contextos-ia/${contexto.id}`, {
        ...contexto,
        activo: !contexto.activo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess(`Contexto ${!contexto.activo ? 'activado' : 'desactivado'} exitosamente`)
      loadContextos()
    } catch (error) {
      setError('Error al cambiar estado: ' + (error.response?.data?.detail || error.message))
    }
  }

  if (loading) {
    return <Typography>Cargando contextos de IA...</Typography>
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Contextos para Prompts de IA
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          Nuevo Contexto
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>💡 Información:</strong> Los contextos de IA definen el marco normativo y educativo que se utiliza
          para generar contenido con inteligencia artificial. Puede configurar diferentes contextos según sus necesidades.
        </Typography>
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contextos.map((contexto) => (
              <TableRow key={contexto.id} hover>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {contexto.nombre}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 400 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {contexto.descripcion}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={contexto.activo ? 'Activo' : 'Inactivo'}
                    color={contexto.activo ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(contexto)}
                      title="Editar"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(contexto)}
                      title={contexto.activo ? 'Desactivar' : 'Activar'}
                      color={contexto.activo ? 'warning' : 'success'}
                    >
                      <SmartToy />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(contexto)}
                      title="Eliminar"
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {contextos.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No hay contextos de IA configurados
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar contexto */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingContexto ? 'Editar Contexto de IA' : 'Nuevo Contexto de IA'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Nombre del Contexto"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              required
              placeholder="Ej: Contexto Educativo ENSAS"
            />

            <TextField
              fullWidth
              label="Descripción"
              multiline
              rows={2}
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Descripción del contexto y su uso"
            />

            <TextField
              fullWidth
              label="Contexto Institucional"
              multiline
              rows={4}
              value={formData.contexto_institucional}
              onChange={(e) => setFormData({ ...formData, contexto_institucional: e.target.value })}
              placeholder="Información sobre la institución, ubicación, sector, marco legal..."
              helperText="Define el contexto específico de la institución educativa"
            />

            <TextField
              fullWidth
              label="Organismos Rectores"
              multiline
              rows={4}
              value={formData.organismos_rectores}
              onChange={(e) => setFormData({ ...formData, organismos_rectores: e.target.value })}
              placeholder="MEN, CESU, CONACES, CNA y sus funciones..."
              helperText="Organismos que rigen la educación superior en Colombia"
            />

            <TextField
              fullWidth
              label="Normatividad Clave"
              multiline
              rows={4}
              value={formData.normatividad_clave}
              onChange={(e) => setFormData({ ...formData, normatividad_clave: e.target.value })}
              placeholder="Decreto 1330 de 2019, Resolución 21795 de 2020, etc..."
              helperText="Normatividad específica de calidad y registro calificado"
            />

            <TextField
              fullWidth
              label="Marco Educativo Específico"
              multiline
              rows={4}
              value={formData.marco_educativo}
              onChange={(e) => setFormData({ ...formData, marco_educativo: e.target.value })}
              placeholder="Decreto Ley 2277 de 1979, Decreto Ley 1278 de 2002..."
              helperText="Estatutos docentes y normatividad educativa específica"
            />

            <TextField
              fullWidth
              label="Planeación Nacional"
              multiline
              rows={3}
              value={formData.planeacion_nacional}
              onChange={(e) => setFormData({ ...formData, planeacion_nacional: e.target.value })}
              placeholder="Ley 2294 de 2023 - Plan Nacional de Desarrollo..."
              helperText="Planes y políticas nacionales vigentes"
            />

            <TextField
              fullWidth
              label="Marco Administrativo Público"
              multiline
              rows={4}
              value={formData.marco_administrativo}
              onChange={(e) => setFormData({ ...formData, marco_administrativo: e.target.value })}
              placeholder="Ley 1437 de 2011, Ley 80 de 1993, Ley 1712 de 2014..."
              helperText="Normatividad administrativa y de contratación pública"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.activo}
                  onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                />
              }
              label="Contexto activo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<Save />}
            disabled={!formData.nombre.trim()}
          >
            {editingContexto ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ContextosIA